import type { LocationPublic } from '@repo/types/locations';
import type { ReactNode } from 'react';

import { useDeleteLocation } from '@repo/api/location/useDeleteLocation';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { BookTextIcon } from 'lucide-react';
import { useEffect, useMemo } from 'react';

import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

/**
 * Props for the DeleteLocation component.
 * @property {LocationPublic[]} [locations] - Array of locations to delete.
 * @property {boolean} open - Whether the dialog is open.
 * @property {(value: boolean) => void} onOpenChange - Callback to change dialog open state.
 */
interface DeleteLocationProps {
  locations?: LocationPublic[];
  open: boolean;
  onOpenChange: (value: boolean) => void;
}

export default function DeleteLocation(deleteLocationProps: DeleteLocationProps) {
  const { locations, open, onOpenChange } = deleteLocationProps;
  const isSingle: boolean = locations?.length === 1;

  const { mutateAsync, reset } = useDeleteLocation();

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, reset]);

  const onSubmit = async () => {
    if (!locations) return;
    await Promise.all(locations.map((element) => mutateAsync(element.id))).catch((error) => {
      toasterMessageHandler(baseErrorMessages, error);
    });
    onClose();
  };

  const onClose = () => {
    onOpenChange(false);
  };

  const header = useMemo<string>(() => {
    if (isSingle) {
      return ' Usunąć Lokację ?';
    } else {
      return `Usunąć ${locations?.length} Lokacje?`;
    }
  }, [isSingle, locations]);

  const content = useMemo<ReactNode>(() => {
    if (isSingle) {
      return (
        <p className={'text-text-secondary'}>
          Czy na pewno chcesz usunąć lokację{'  '}
          <b>{locations?.[0]?.name}</b>?
        </p>
      );
    } else {
      return <p className={'text-text-secondary'}>Czy na pewno chcesz usunąć wybrane lokacje ?</p>;
    }
  }, [isSingle, locations]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className={'sm:max-w-96'}>
        <AlertDialogBody>
          <AlertDialogHeader>
            <FeaturedIcon>
              <BookTextIcon />
            </FeaturedIcon>
            <div className={'flex min-w-0 grow flex-col gap-0.5'}>
              <AlertDialogTitle>{header}</AlertDialogTitle>
              <AlertDialogDescription className={'text-muted-foreground text-xs'}>Usuń wybrane lokacje.</AlertDialogDescription>
            </div>
          </AlertDialogHeader>
          <div className={'flex flex-col gap-3 overflow-auto px-4'}>
            {content}
            {!isSingle && locations && (
              <div className={'flex flex-col gap-3'}>
                <h5 className={'text-muted-foreground text-sm font-medium'}>Lokacje:</h5>
                <div className={'flex flex-wrap gap-2'}>
                  {locations.map(({ name, id }) => {
                    return (
                      <Badge key={id} size={'sm'} variant={'muted'}>
                        {name}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </AlertDialogBody>
        <AlertDialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
          <AlertDialogCancel variant={'tertiary'} onClick={onClose}>
            Zamknij
          </AlertDialogCancel>
          <AlertDialogAction onClick={onSubmit}>Potwierdź</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
